import React from 'react';
import {useFloating, offset, flip, shift, autoUpdate} from '@floating-ui/react';
import {Popover, PopoverButton, PopoverPanel} from '@headlessui/react';
import {InformationCircleIcon} from '@heroicons/react/24/outline';
import {createPortal} from 'react-dom';
import {classNames} from 'utils';

interface IInfoProps {
  content?: string | React.ReactNode;
  button?: React.ReactNode;
}

const Info = ({content, button}: IInfoProps) => {
  const {x, y, refs, strategy} = useFloating({
    placement: 'top',
    middleware: [offset(10), flip(), shift()],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });
  return (
    <Popover as="div" className="relative">
      {({open: _, close}) => {
        return (
          <>
            <PopoverButton
              ref={refs.setReference}
              onMouseEnter={e => e.currentTarget.click()}
              onMouseLeave={() => close()}
              className="flex items-center">
              {button ?? <InformationCircleIcon className="size-5 text-primary" />}
            </PopoverButton>
            {createPortal(
              <PopoverPanel
                transition
                ref={refs.setFloating}
                className={classNames(
                  'absolute right-0 top-10 z-10 mt-2 origin-top-right divide-y divide-gray-100 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-500 shadow-lg transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in',
                  'w-full max-w-xs sm:max-w-sm',
                )}
                style={{
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                  zIndex: 50,
                }}>
                <div className="py-1 px-3">
                  {(() => {
                    if (typeof content === 'string') return <span className="text-slate-800 dark:text-white text-sm">{content}</span>;
                    return content;
                  })()}
                </div>
              </PopoverPanel>,
              document.body,
            )}
          </>
        );
      }}
    </Popover>
  );
};

export default React.memo(Info);
